/**
 * The developer track's sign-in.
 *
 * Two states, because an operator creates an account with a password the reader
 * did not choose: sign in, then — if `must_reset` — choose one. The issued
 * password is single-use in practice without a second token to email, lose, or
 * leak into a support thread.
 *
 * The error text is deliberately identical for an unknown address and a wrong
 * password. This portal's account list is a list of our customers' engineers, and
 * a login form that distinguishes the two is an enumeration oracle for it.
 */

import { useState, type ReactNode } from 'react';
import { choosePassword, login, PortalError } from './api.js';
import type { Session } from './types.js';

export function Gate({ onSignedIn }: { onSignedIn: (session: Session) => void }): ReactNode {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set once sign-in succeeds but the account still owes us a chosen password. */
  const [pending, setPending] = useState<Session | null>(null);
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const who = await login(email.trim(), password);
      if (who.must_reset) setPending(who);
      else onSignedIn(who);
    } catch (e) {
      setError(e instanceof PortalError ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  async function setNewPassword(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    if (next !== confirm) {
      setError('Those two do not match.');
      return;
    }
    if (next.length < 12) {
      setError('At least 12 characters.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await choosePassword(next);
      onSignedIn({ ...pending!, must_reset: false });
    } catch (e) {
      setError(e instanceof PortalError ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  if (pending) {
    return (
      <div className="gate">
        <div className="card tinted k-violet">
          <span className="top-accent" />
          <h2>Choose a password</h2>
          <p>
            The one you signed in with was issued by an operator and is now spent. Pick your own —
            at least 12 characters.
          </p>
          {error ? <div className="err">{error}</div> : null}
          <form onSubmit={setNewPassword}>
            <div className="field">
              <label htmlFor="np">New password</label>
              <input
                id="np"
                type="password"
                value={next}
                autoComplete="new-password"
                onChange={(e) => setNext(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="cp">Confirm</label>
              <input
                id="cp"
                type="password"
                value={confirm}
                autoComplete="new-password"
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <button className="btn" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save and continue'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="gate">
      <div className="card tinted k-teal">
        <span className="top-accent" />
        <h2>Developer documentation</h2>
        <p>
          Integration guides, the full API reference, and your own client ids. Your account was
          created by your Nuqi contact.
        </p>
        {error ? <div className="err">{error}</div> : null}
        <form onSubmit={submit}>
          <div className="field">
            <label htmlFor="em">Work email</label>
            <input
              id="em"
              type="email"
              value={email}
              autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="pw">Password</label>
            <input
              id="pw"
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button className="btn" type="submit" disabled={busy || !email || !password}>
            {busy ? 'Checking…' : 'Sign in'}
          </button>
        </form>
        <p style={{ marginTop: 16, marginBottom: 0, fontSize: 13 }}>
          Everything on the <a href="/business">business track</a> is public — what the product
          does, how it is priced, and what a rollout looks like.
        </p>
      </div>
    </div>
  );
}
